import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { documentAPI, companyAPI } from '../../services/api';
import { PDF_EDIT_SCHEMAS, resolvePdfData, fixedFieldKeys } from '../PDF/pdfEditSchemas';

// Generic edit form for the data printed onto a generated PDF document
// (certificates, letters, advices...). Works exactly like the Truck Receipt
// editor: prefilled from the saved print data if any, otherwise from
// defaults derived from the commercial invoice; "Reset from Invoice"
// refills every field from the invoice again.
export default function PdfDataEditModal({ docId, docType, onClose, onSaved }) {
  const schema = PDF_EDIT_SCHEMAS[docType];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [invoiceDefaults, setInvoiceDefaults] = useState(null);

  useEffect(() => {
    if (!schema) {
      onClose();
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const docRes = await documentAPI.getById(docId);
        const fullDoc = docRes.data;
        let company = {};
        let authority = null;
        try {
          const compRes = await companyAPI.getById(fullDoc.company_id);
          company = compRes.data || {};
          authority = (company.authorities || []).find(
            (a) => a.is_primary && a.status === 'Active'
          ) || (company.authorities || [])[0] || null;
        } catch (_) { /* defaults still work without company */ }

        const resolved = resolvePdfData(fullDoc, company, authority, docType);
        const fresh = resolvePdfData(fullDoc, company, authority, docType, null);
        if (!cancelled) {
          setForm(resolved);
          setInvoiceDefaults(fresh);
        }
      } catch (err) {
        console.error('Failed to load PDF data:', err);
        toast.error(err.response?.data?.error || 'Failed to load document data');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [docId, docType]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!schema) return null;

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async (preview) => {
    setSaving(true);
    try {
      // Fields the invoice writes are shown, not edited — they are left out of
      // the save so they keep following the invoice.
      const printData = { ...form };
      fixedFieldKeys(docType).forEach((key) => { delete printData[key]; });
      await documentAPI.savePdfData(docId, docType, printData);
      toast.success(`${schema.title} data saved`);
      onClose();
      if (preview && onSaved) onSaved();
    } catch (err) {
      console.error('Failed to save PDF data:', err);
      toast.error(err.response?.data?.error || 'Failed to save document data');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: '#047857',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };
  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: 8,
    fontSize: 13,
    color: '#1f2937',
    fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
    background: '#fff',
  };

  const fixedStyle = {
    ...inputStyle,
    background: '#f8fdfb',
    color: '#4b5563',
    border: '1px dashed rgba(16,185,129,0.35)',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
  };
  const hintStyle = { fontSize: 11, color: '#6b7280', marginTop: 4 };

  const renderField = (field) => {
    // Printed straight from the invoice: shown as it will print, with no box
    // to type in — change it on the invoice itself.
    if (field.fixed) {
      return (
        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>{field.label}</label>
          <div style={fixedStyle}>{form[field.key] || ''}</div>
          <div style={hintStyle}>Follows the invoice — edit the invoice to change it.</div>
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>{field.label}</label>
          <textarea
            rows={field.rows || 3}
            value={form[field.key] || ''}
            onChange={set(field.key)}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>
      );
    }
    return (
      <div key={field.key}>
        <label style={labelStyle}>{field.label}</label>
        <input
          type="text"
          value={form[field.key] || ''}
          onChange={set(field.key)}
          style={inputStyle}
        />
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(6,78,59,0.25)',
            width: '100%',
            maxWidth: 680,
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '18px 24px',
            borderBottom: '1px solid rgba(16,185,129,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#064e3b', fontFamily: "'Poppins', sans-serif" }}>
                {schema.icon} Edit {schema.title}
              </div>
              <div style={{ fontSize: 12, color: '#047857', marginTop: 2 }}>
                Data printed on this document — unedited fields keep following the invoice
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(6,78,59,0.08)',
                border: 'none',
                borderRadius: 8,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#064e3b',
                fontSize: 18,
              }}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: 20, overflowY: 'auto' }}>
            {loading || !form ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                Loading document data…
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {schema.fields.map(renderField)}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '14px 24px',
            borderTop: '1px solid rgba(16,185,129,0.15)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: '#f8fdfb',
          }}>
            <button
              onClick={() => invoiceDefaults && setForm(invoiceDefaults)}
              disabled={saving || loading || !invoiceDefaults}
              title="Refill every field from the invoice data (discards edits until you save)"
              style={{
                marginRight: 'auto',
                padding: '9px 14px',
                borderRadius: 8,
                border: '1px solid rgba(16,185,129,0.3)',
                background: '#fff',
                color: '#047857',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ↺ Reset from Invoice
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '9px 18px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving || loading}
              style={{
                padding: '9px 18px',
                borderRadius: 8,
                border: '1px solid rgba(16,185,129,0.4)',
                background: '#ecfdf5',
                color: '#047857',
                fontSize: 13,
                fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              Save & Preview
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving || loading}
              style={{
                padding: '9px 22px',
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer',
                boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
